import {
  type BindingChannel,
  type BindingCodeRequest,
  type BindingErrorCode,
  type BindingVerifyRequest,
  bindingChannels,
  bindingDemo,
  type ContactBinding,
  maskBindingValue,
} from '../../src/pages/account/shared/binding';
import type {
  AccountNotification,
  AccountProfile,
  NotificationParams,
  NotificationSettings,
  PasswordUpdate,
  ProfileUpdate,
  SecuritySettings,
} from '../../src/pages/account/shared/types';
import {
  hasPlatformPermission,
  type PlatformPermission,
  platformPermissions,
} from '../../src/services/business/permissions';
import { cloneJson } from '../../src/utils/cloneJson';
import { defaultUser } from '../utils';

const standardUserPermissions: PlatformPermission[] = [
  platformPermissions.announcementsRead,
  platformPermissions.dashboardRead,
  platformPermissions.logsRead,
  platformPermissions.usersRead,
];

function text(value: unknown, max: number, required = false) {
  if (typeof value !== 'string' || value.length > max || (required && !value.trim()))
    throw new Error('请检查必填内容和长度 / Invalid field');
  return value.trim();
}
function email(value: unknown, required = false) {
  const result = text(value, 120, required);
  if (result && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(result))
    throw new Error('邮箱格式不正确 / Invalid email');
  return result;
}
function phone(value: unknown) {
  const result = text(value, 30);
  if (result && !/^[+\d][\d ()-]{5,29}$/.test(result))
    throw new Error('电话格式不正确 / Invalid phone');
  return result;
}

/** 绑定流程的失败都带稳定错误码，前端据此展示本地化文案。 */
class BindingError extends Error {
  constructor(
    readonly code: BindingErrorCode,
    message: string,
  ) {
    super(message);
    this.name = 'BindingError';
  }
}

export function createAccountStore() {
  type BindingChallenge = {
    channel: BindingChannel;
    target: string;
    sentAt: number;
    attempts: number;
  };
  type FakeUser = {
    profile: AccountProfile;
    notifications: AccountNotification[];
    password: string;
    security: SecuritySettings;
    subscriptions: NotificationSettings;
    /** 演示验证状态，初始全部未验证，不代表真实身份验证。 */
    verifiedChannels: Record<BindingChannel, boolean>;
    /** 每个用户最多保留一个进行中的挑战，切换会话不会互相干扰。 */
    challenge: BindingChallenge | null;
  };
  const users = new Map(
    ['admin', 'user'].map<[string, FakeUser]>((username, index) => {
      const id = index ? '00000002' : defaultUser.userid;
      const profile: AccountProfile = {
        id,
        username,
        displayName: index ? 'Demo User' : defaultUser.name,
        avatar: defaultUser.avatar,
        email: index ? 'user@example.com' : defaultUser.email,
        bio: defaultUser.signature,
        region: '中国 · 杭州',
        phone: defaultUser.phone,
        roles: [index ? 'user' : 'admin'],
        createdAt: '2026-01-01T00:00:00.000Z',
        version: 1,
      };
      const notifications: AccountNotification[] = Array.from(
        { length: 9 },
        (_, i) => ({
          id: `${id}-${i + 1}`,
          title: ['欢迎使用管理后台', '系统维护通知', '待办任务提醒'][i % 3],
          titleEn: [
            'Welcome to the admin workspace',
            'Scheduled maintenance',
            'Task reminder',
          ][i % 3],
          content: `${profile.displayName}，这是您的第 ${i + 1} 条通知，可标记已读并在通知中心回查。`,
          contentEn: `Notification ${i + 1} for ${profile.displayName}. Read status is shared with the notification center.`,
          demo: { recipient: profile.displayName, number: i + 1 },
          createdAt: new Date(Date.now() - i * 3600000).toISOString(),
          readAt: i < 7 ? null : new Date().toISOString(),
        }),
      );
      return [
        username,
        {
          profile,
          notifications,
          password: 'ant.design',
          security: { recoveryEmail: '' },
          subscriptions: { systemMessage: true, todoTask: true, userMessage: true },
          verifiedChannels: { email: false, phone: false, recoveryEmail: false },
          challenge: null,
        },
      ];
    }),
  );
  let session: string | null = 'admin';
  function current() {
    const user = session ? users.get(session) : undefined;
    if (!user) throw new Error('请先登录 / Sign in required');
    return user;
  }
  const copy = <T>(value: T): T => cloneJson(value);
  function bindingValue(user: FakeUser, channel: BindingChannel) {
    if (channel === 'email') return user.profile.email;
    if (channel === 'phone') return user.profile.phone;
    return user.security.recoveryEmail;
  }
  function bindingSnapshot(user: FakeUser, channel: BindingChannel): ContactBinding {
    return {
      channel,
      maskedValue: maskBindingValue(channel, bindingValue(user, channel)),
      verified: user.verifiedChannels[channel],
    };
  }
  function bindingTarget(channel: unknown, target: unknown) {
    if (!bindingChannels.includes(channel as BindingChannel))
      throw new BindingError('invalid_target', '绑定目标无效 / Invalid binding target');
    const selected = channel as BindingChannel;
    try {
      return {
        channel: selected,
        target:
          selected === 'phone'
            ? phone(text(target, 30, true))
            : email(target, true).toLowerCase(),
      };
    } catch (error) {
      throw new BindingError(
        'invalid_target',
        error instanceof Error
          ? error.message
          : '绑定目标无效 / Invalid binding target',
      );
    }
  }
  return {
    login(username: string, password: string) {
      const user = users.get(username);
      if (!user || user.password !== password) {
        session = null;
        return false;
      }
      session = username;
      return true;
    },
    loginMobile() {
      session = 'admin';
    },
    logout() {
      if (session) {
        const user = users.get(session);
        if (user) user.challenge = null;
      }
      session = null;
    },
    currentUser() {
      if (!session) {
        return { isLogin: false, isSuperAdmin: false, permissions: [] };
      }
      const { profile } = current();
      const isSuperAdmin = profile.roles.includes('admin');
      return {
        ...defaultUser,
        isLogin: true,
        userid: profile.id,
        name: profile.displayName,
        avatar: profile.avatar,
        email: profile.email,
        signature: profile.bio,
        phone: profile.phone,
        access: profile.roles[0],
        isSuperAdmin,
        permissions: isSuperAdmin ? [] : [...standardUserPermissions],
      };
    },
    hasPermission(permission: PlatformPermission) {
      const user = current();
      return hasPlatformPermission(
        {
          isSuperAdmin: user.profile.roles.includes('admin'),
          permissions: standardUserPermissions,
        },
        permission,
      );
    },
    profile: () => copy(current().profile),
    updateProfile(input: ProfileUpdate) {
      const user = current();
      if (input.expectedVersion !== user.profile.version)
        throw new Error('资料已更新，请刷新后重试 / Refresh before saving');
      // 邮箱与手机号是绑定目标，只接受通过验证的 bindings 流程更新。
      const fields = {
        displayName: text(input.displayName, 40, true),
        bio: text(input.bio, 200),
        region: text(input.region, 80),
      };
      Object.assign(user.profile, fields, { version: user.profile.version + 1 });
      return copy(user.profile);
    },
    updateAvatar(dataUrl: string) {
      const user = current();
      const match =
        typeof dataUrl === 'string' &&
        /^data:image\/(png|jpeg|webp);base64,([A-Za-z0-9+/]+={0,2})$/.exec(dataUrl);
      if (!match) throw new Error('仅支持 PNG/JPEG/WebP / Invalid image type');
      if (match[2].length > Math.ceil((2 * 1024 * 1024) / 3) * 4)
        throw new Error('图片超过2MiB / Image is larger than 2MiB');
      // Fake 同时运行在开发服务与生产浏览器拦截器中，使用两端共有的解码接口。
      const binary = atob(match[2]);
      const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
      const valid =
        match[1] === 'png'
          ? bytes.length > 24 &&
            [137, 80, 78, 71, 13, 10, 26, 10].every(
              (byte, index) => bytes[index] === byte,
            )
          : match[1] === 'jpeg'
            ? bytes.length > 4 &&
              bytes[0] === 255 &&
              bytes[1] === 216 &&
              bytes[bytes.length - 2] === 255 &&
              bytes[bytes.length - 1] === 217
            : bytes.length > 12 &&
              binary.slice(0, 4) === 'RIFF' &&
              binary.slice(8, 12) === 'WEBP';
      if (!valid || bytes.length > 2 * 1024 * 1024)
        throw new Error('图片无效或超过2MiB / Invalid or oversized image');
      user.profile.avatar = dataUrl;
      user.profile.version++;
      return copy(user.profile);
    },
    security: () => copy(current().security),
    bindings(): ContactBinding[] {
      const user = current();
      return bindingChannels.map((channel) => bindingSnapshot(user, channel));
    },
    /** 发码只登记挑战，不发送真实短信或邮件。 */
    sendBindingCode(input: BindingCodeRequest) {
      const user = current();
      const { channel, target } = bindingTarget(input.channel, input.target);
      const now = Date.now();
      const active = user.challenge;
      if (
        active &&
        active.channel === channel &&
        active.target === target &&
        now - active.sentAt < bindingDemo.cooldownSeconds * 1000
      )
        throw new BindingError(
          'cooldown',
          '发送过于频繁，请稍后重试 / Wait before requesting another code',
        );
      // 换目标或重新发码都会替换挑战，旧验证码随即失效。
      user.challenge = { channel, target, sentAt: now, attempts: 0 };
      return {
        channel,
        maskedTarget: maskBindingValue(channel, target),
        expiresInSeconds: bindingDemo.codeTtlSeconds,
        cooldownSeconds: bindingDemo.cooldownSeconds,
        attemptsLeft: bindingDemo.maxAttempts,
      };
    },
    verifyBinding(input: BindingVerifyRequest) {
      const user = current();
      const { channel, target } = bindingTarget(input.channel, input.target);
      const active = user.challenge;
      if (!active || active.channel !== channel)
        throw new BindingError('no_challenge', '请先获取验证码 / Request a code first');
      if (active.target !== target)
        throw new BindingError(
          'target_mismatch',
          '目标已更改，请重新获取验证码 / Target changed; request a new code',
        );
      if (active.attempts >= bindingDemo.maxAttempts)
        throw new BindingError(
          'challenge_locked',
          '错误次数过多，请重新获取验证码 / Too many attempts; request a new code',
        );
      if (Date.now() - active.sentAt >= bindingDemo.codeTtlSeconds * 1000)
        throw new BindingError(
          'code_expired',
          '验证码已过期，请重新获取 / The code has expired',
        );
      if (input.code !== bindingDemo.code) {
        active.attempts++;
        throw active.attempts >= bindingDemo.maxAttempts
          ? new BindingError(
              'challenge_locked',
              '错误次数过多，请重新获取验证码 / Too many attempts; request a new code',
            )
          : new BindingError('invalid_code', '验证码不正确 / Incorrect code');
      }
      if (channel === 'recoveryEmail') user.security.recoveryEmail = target;
      else {
        user.profile[channel] = target;
        user.profile.version++;
      }
      user.verifiedChannels[channel] = true;
      user.challenge = null;
      return {
        binding: bindingSnapshot(user, channel),
        profileVersion: user.profile.version,
      };
    },
    subscriptions: () => copy(current().subscriptions),
    updateSubscriptions(input: NotificationSettings) {
      const user = current();
      for (const key of ['systemMessage', 'todoTask', 'userMessage'] as const)
        if (typeof input[key] !== 'boolean')
          throw new Error('通知选项无效 / Invalid setting');
      user.subscriptions = {
        systemMessage: input.systemMessage,
        todoTask: input.todoTask,
        userMessage: input.userMessage,
      };
      return copy(user.subscriptions);
    },
    changePassword(input: PasswordUpdate) {
      const user = current();
      if (input.currentPassword !== user.password)
        throw new Error('当前密码不正确 / Incorrect current password');
      if (
        typeof input.newPassword !== 'string' ||
        input.newPassword.length < 8 ||
        input.newPassword.length > 64 ||
        !/[a-zA-Z]/.test(input.newPassword) ||
        !/\d/.test(input.newPassword)
      )
        throw new Error(
          '新密码须为8–64位并包含字母和数字 / Use 8–64 characters with letters and numbers',
        );
      user.password = input.newPassword;
      return { updated: true };
    },
    notifications(params: NotificationParams) {
      const all = current().notifications;
      const page = Math.max(1, Math.floor(Number(params.page) || 1));
      const pageSize = Math.max(
        1,
        Math.min(50, Math.floor(Number(params.pageSize) || 10)),
      );
      const filtered = params.unread ? all.filter((item) => !item.readAt) : all;
      return {
        items: copy(filtered.slice((page - 1) * pageSize, page * pageSize)),
        total: filtered.length,
        page,
        pageSize,
        unreadCount: all.filter((item) => !item.readAt).length,
      };
    },
    read(id: string) {
      const item = current().notifications.find((item) => item.id === id);
      if (!item) throw new Error('通知不存在 / Notification not found');
      item.readAt ??= new Date().toISOString();
      return copy(item);
    },
    readAll() {
      const items = current().notifications.filter((item) => !item.readAt);
      for (const item of items) item.readAt = new Date().toISOString();
      return { updated: items.length, unreadCount: 0 };
    },
  };
}

// 认证与账户路由共用同一开发会话，不借用默认用户的硬编码未读数。
declare global {
  var __reactAntdAdminAccountStore: ReturnType<typeof createAccountStore> | undefined;
}

if (!globalThis.__reactAntdAdminAccountStore) {
  globalThis.__reactAntdAdminAccountStore = createAccountStore();
}

export const accountStore = globalThis.__reactAntdAdminAccountStore;
